import { z } from 'zod'
import { oc } from '@orpc/contract'
import { artifactsSchema } from '../../model/artifacts/artifacts.schema'

/**
 * The patient's OWN clinical summary overview, for the v2 portal's Health Record page.
 *
 * A deliberate sibling of `/v2/patient/{id}/clinical-summary-overview` rather than a reuse
 * of it: that route takes a caller-supplied patient id, which `@UserAuthClient()` alone does
 * not entitle the caller to. This one derives the subject from the authenticated profile,
 * so a patient can only ever read their own.
 *
 * The path deliberately ends `clinical-summary`, not `clinical-summary-overview`: the latter
 * would be a fully literal path shadowed by that `{id}` sibling, since `{id}` matches the
 * literal `me`. Which one served the request would then depend on registration order — and
 * the `{id}` route runs under an admin client, so losing that race would route a patient's
 * own request onto the surface this endpoint exists to keep them off. `contracts.spec.ts`
 * enforces the rule; this path keeps the two from overlapping at all.
 *
 * The wire is also deliberately narrower than the provider route's, in two ways.
 *
 * The envelope: staleness, the being-superseded flag, the live build feed and the failure
 * marker all drive the provider-side refresh controls; the portal renders the stored summary
 * or nothing, so none of that state is exposed here. `summary` is null when the patient has
 * no overview yet (or only a contentless placeholder while a first build runs).
 *
 * The artifacts: an explicit allow-list of what the portal paints (`HealthSnapshot` reads
 * `answer` and `kpis`, nothing else), rather than the provider route's whole blob. The rest
 * is written for clinicians -- `followUpSuggestions` are questions a PROVIDER could ask next,
 * and the tables carry ICD-10/SNOMED-coded problems and social determinants (smoking, alcohol
 * and substance use, occupation) -- so serializing it to the patient's browser would ship
 * uncontextualised clinical content that nothing renders and that carries none of the
 * disclaimer the summary prose does. `specials` is a passthrough schema, so it would also
 * carry arbitrary unreviewed fields verbatim. Widen this only as portal surfaces render more.
 */
/**
 * The artifacts a patient receives: an allow-list of what `HealthSnapshot` paints. Named so
 * the boundary is testable on its own rather than only through the route.
 */
export const patientPortalArtifactsSchema = artifactsSchema.pick({ answer: true, kpis: true })

export const patientMeClinicalSummaryOverview = oc
  .route({ method: 'GET', path: '/v2/patient/me/clinical-summary' })
  .input(z.object({}))
  .output(
    z.object({
      summary: z
        .object({
          generatedAt: z.string(),
          artifacts: patientPortalArtifactsSchema,
        })
        .nullable(),
    }),
  )
